'use client'

import { cn } from '@brew/ui/cn'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { useMemo, useState } from 'react'
import { useDebounce } from '@/hooks/use-debounce'
import {
  getClientBrandFaviconUrl,
  getClientBrandLogoUrl,
} from '@/lib/brand/client-brand-logo-url'
import { sanitizeDomain } from '@/lib/brand/primitives/domain-utils'

export type BrandUrlInputLogoProps = {
  domainInput: string
  className?: string
  /** Classes for the `<img>` itself (e.g. a circular crop). */
  imageClassName?: string
}

const LOGO_DEBOUNCE_MS = 250

type BrandUrlInputLogoImageProps = {
  domain: string
  className?: string
  imageClassName?: string
}

function BrandUrlInputLogoImage({
  domain,
  className,
  imageClassName,
}: BrandUrlInputLogoImageProps) {
  const shouldReduceMotion = useReducedMotion() ?? false
  const [fallbackStage, setFallbackStage] = useState(0)

  const src = useMemo(() => {
    if (fallbackStage === 0) {
      return getClientBrandLogoUrl(domain)
    }
    if (fallbackStage === 1) {
      return getClientBrandFaviconUrl(domain)
    }
    return ''
  }, [domain, fallbackStage])

  const isVisible = fallbackStage < 2 && src.length > 0

  const motionProps = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 0.92 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.92 },
        transition: { duration: 0.18 },
      }

  return (
    <AnimatePresence mode="popLayout">
      {isVisible ? (
        <motion.span
          key={`${domain}-${fallbackStage}`}
          className={cn(
            'flex size-6 shrink-0 items-center justify-center',
            className
          )}
          {...motionProps}
        >
          {/* biome-ignore lint/performance/noImgElement: context.dev Logo Link (hotlink-only CDN) */}
          {/** biome-ignore lint/correctness/useImageSize: pre-Ultracite baseline — add dimensions when touching */}
          <img
            src={src}
            alt=""
            className={cn('size-6 rounded object-contain', imageClassName)}
            decoding="async"
            onError={() => setFallbackStage((stage) => stage + 1)}
          />
        </motion.span>
      ) : null}
    </AnimatePresence>
  )
}

export function BrandUrlInputLogo({
  domainInput,
  className,
  imageClassName,
}: BrandUrlInputLogoProps) {
  const sanitized = useMemo(
    () => sanitizeDomain(domainInput.trim()) ?? '',
    [domainInput]
  )
  const displayDomain = useDebounce(sanitized, LOGO_DEBOUNCE_MS)

  if (!displayDomain) {
    return null
  }

  return (
    <BrandUrlInputLogoImage
      key={displayDomain}
      domain={displayDomain}
      className={className}
      imageClassName={imageClassName}
    />
  )
}
